import React, { useState, useMemo } from "react";
import { 
  Card, 
  CardBody, 
  Nav, 
  NavItem, 
  NavLink, 
  TabContent, 
  TabPane, 
  Spinner,
  Input,
  InputGroup,
  InputGroupText,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge
} from "reactstrap";
import { Search, Filter } from "lucide-react";
import './index.css'
import { OverviewTab } from "./OverviewTab";
import { ByChannelTab } from "./ByChannelTab";
import { ByCartTab } from "./ByCartTab";
import { CartChannelTab } from "./CartChannelTab";
import { FullPictureTab } from "./FullPictureTab";
import { ProductOverviewItem } from "../../../../redux/services/allocationService";
import { ProductSummary } from "../../../../hooks/useFirebaseProductSummaries";
import { extractTierFromName } from "../../../../utility/inventoryUtils";

interface InventoryTabsProps {
  overviewProducts: ProductOverviewItem[];
  productSummaries?: ProductSummary[];
}

// Tier options including "No tier"
const TIER_OPTIONS = [
  { value: "all", label: "All Tiers" },
  { value: "bronze", label: "Bronze" },
  { value: "gold", label: "Gold" },
  { value: "platinum", label: "Platinum" },
  { value: "no-tier", label: "No Tier" },
];

export const InventoryTabs: React.FC<InventoryTabsProps> = ({
  overviewProducts,
  productSummaries = [],
}) => {
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTier, setSelectedTier] = useState<string>("all");
  const [filterDropdownOpen, setFilterDropdownOpen] = useState<boolean>(false);

  const tabs = [
    { 
      id: "overview", 
      label: "Overview", 
      component: OverviewTab,
    },
    { 
      id: "by-channel", 
      label: "By Channel", 
      component: ByChannelTab,
    },
    { 
      id: "by-cart", 
      label: "By Cart", 
      component: ByCartTab,
    },
    { 
      id: "cart-channel", 
      label: "Cart-Channel Detail", 
      component: CartChannelTab,
    },
    { 
      id: "full-picture", 
      label: "Full Picture", 
      component: FullPictureTab,
    },
  ];

  // Filter overview products based on search term and tier
  const filteredOverviewProducts = useMemo(() => {
    return overviewProducts.filter(product => {
      // Search filter
      const matchesSearch = searchTerm === "" || 
        product.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.productId?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Tier filter
      if (selectedTier === "all") return true;
      
      const tier = extractTierFromName(product.displayName || "");
      
      if (selectedTier === "no-tier") {
        return tier.toLowerCase() === "no tier";
      }
      
      return tier.toLowerCase() === selectedTier.toLowerCase();
    });
  }, [overviewProducts, searchTerm, selectedTier]);

  // Filter product summaries based on search term and tier
  const filteredProductSummaries = useMemo(() => {
    return productSummaries.filter(product => {
      // Search filter
      const matchesSearch = searchTerm === "" || 
        product.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.productId?.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;
      
      // Tier filter
      if (selectedTier === "all") return true;
      
      const tier = extractTierFromName(product.displayName || "");
      
      if (selectedTier === "no-tier") {
        return tier.toLowerCase() === "no tier";
      }
      
      return tier.toLowerCase() === selectedTier.toLowerCase();
    });
  }, [productSummaries, searchTerm, selectedTier]);

  const renderTabContent = (tabId: string) => {
   
    
    switch (tabId) {
      case "overview":
        return <OverviewTab products={filteredOverviewProducts} />;
      case "by-channel":
        return <ByChannelTab productSummaries={filteredProductSummaries} />;
      case "by-cart":
        return <ByCartTab productSummaries={filteredProductSummaries} />;
      case "cart-channel":
        return <CartChannelTab productSummaries={filteredProductSummaries} />;
      case "full-picture":
        return <FullPictureTab productSummaries={filteredProductSummaries} />;
      default:
        return null;
    }
  };

  // Get badge color for selected tier
  const getTierBadgeColor = (tier: string) => {
    switch (tier.toLowerCase()) {
      case "bronze": return "warning";
      case "gold": return "success";
      case "platinum": return "primary";
      case "no-tier": return "secondary";
      default: return "light";
    }
  };

  const selectedTierOption = TIER_OPTIONS.find(option => option.value === selectedTier);

  return (
    <Card className="inventory-tabs-container-card">
      <CardBody className="inventory-tabs-container-card-body">
        {/* Search and Filter Bar */}
        <div className="inventory-search-filter-bar mb-4">
          <div className="row align-items-center">
            <div className="col-md-8 mb-3 mb-md-0">
              <InputGroup>
                <InputGroupText className="bg-light border-end-0">
                  <Search size={18} />
                </InputGroupText>
                <Input
                  type="text"
                  placeholder="Search products by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border-start-0"
                />
                {searchTerm && (
                  <button
                    className="btn btn-outline-secondary border-start-0"
                    onClick={() => setSearchTerm("")}
                    type="button"
                    style={{ 
                      borderTopLeftRadius: 0, 
                      borderBottomLeftRadius: 0,
                      borderLeft: 'none'
                    }}
                  >
                    Clear
                  </button>
                )}
              </InputGroup>
            </div>
            <div className="col-md-4">
              <div className="d-flex align-items-center">
                <span className="me-2 text-muted small">Filter by:</span>
                <Dropdown
                  isOpen={filterDropdownOpen}
                  toggle={() => setFilterDropdownOpen(!filterDropdownOpen)}
                  className="flex-grow-1"
                >
                  <DropdownToggle
                    caret
                    className="d-flex justify-content-between align-items-center w-100"
                    color="light"
                  >
                    <div className="d-flex align-items-center">
                      <Filter size={16} className="me-2" />
                      <span>{selectedTierOption?.label}</span>
                    </div>
                  </DropdownToggle>
                  <DropdownMenu className="w-100">
                    {TIER_OPTIONS.map((option) => (
                      <DropdownItem
                        key={option.value}
                        onClick={() => setSelectedTier(option.value)}
                        active={selectedTier === option.value}
                      >
                        {option.label}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
                {selectedTier !== "all" && (
                  <Badge 
                    color={getTierBadgeColor(selectedTier)} 
                    className="ms-2"
                    pill
                  >
                    {selectedTierOption?.label}
                  </Badge>
                )}
              </div>
            </div>
          </div>
     
        </div>

        {/* Tabs Navigation */}
        <Nav tabs className="inventory-tabs-nav mb-3">
          {tabs.map((tab) => (
            <NavItem key={tab.id}>
              <NavLink
                className={`inventory-tab-nav-link ${
                  activeTab === tab.id ? "active" : ""
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {filteredProductSummaries.length !== productSummaries.length && 
                 tab.id !== "overview" && (
                  <Badge color="info" pill className="ms-2">
                    {filteredProductSummaries.length}
                  </Badge>
                )}
                {filteredOverviewProducts.length !== overviewProducts.length && 
                 tab.id === "overview" && (
                  <Badge color="info" pill className="ms-2">
                    {filteredOverviewProducts.length}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {/* Tab Content */}
        <TabContent activeTab={activeTab}>
          {tabs.map((tab) => (
            <TabPane key={tab.id} tabId={tab.id}>
              {activeTab === tab.id ? (
                renderTabContent(tab.id)
              ) : (
                <div className="text-center py-3">
                  <Spinner size="sm" /> Loading {tab.label} data...
                </div>
              )}
            </TabPane>
          ))}
        </TabContent>
      </CardBody>
    </Card>
  );
};